import { readUnhandledEnumCase } from 'rpc-client-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Text } from 'foundations-components/components/Text';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import ACLessonNavigator from 'academy-common-lib/components/navigation/contentNavigator/ACLessonNavigator';
import { mapLessonContentTypenameToType } from 'academy-common-lib/features/lessons/lessonUtils';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { ChapterAssetTypename, VideoAssetTypename } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService';
import CardMenuIcon from 'academy-common-lib/features/cardMenu/components/CardMenuIcon';
import ToolRecommendationOptionalStep from '../../features/toolRecommendations/components/ToolRecommendationOptionalStep';
import { PRE_ASSESSMENT_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS } from '../../constants/Navigation';
import { HasGate } from '../../features/accounts/components/HasGate';
import { useLessonQuizMeVideoRequestParams } from '../../features/interactiveEducation/hooks/useLessonQuizMe';
import { useLessonPreQuizSession } from '../../features/preAssessmentQuiz/hooks/useLessonPreQuiz';
import { LessonPreQuizOptionalTab } from '../../features/preAssessmentQuiz/components/LessonPreQuizOptionalTab';
import { showPreAssessmentFocusRibbon } from '../../features/preAssessmentQuiz/utils/preAssessmentQuizUtils';
import { isLinkAsset, isPracticalExerciseAsset, isQuizAsset } from '../../features/tracks/tracksUtils';
import { getHumanizedDuration } from '../../utils/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getAssetTitle = asset => {
  const contentType = mapLessonContentTypenameToType(asset);
  if (contentType === 'QUIZ') {
    return I18n.text('learningCenter.lessonNavigator.quiz');
  }
  if (contentType === 'PRACTICAL_EXERCISE_FLOW') {
    return I18n.text('learningCenter.practicalExercise.practicalExercise');
  }
  return asset.name;
};
const getAssetHelpText = asset => {
  if (isQuizAsset(asset)) {
    return I18n.text('learningCenter.quiz.numQuestions', {
      count: asset.length || 0
    });
  }
  if (isPracticalExerciseAsset(asset)) {
    return asset.name;
  }
  if (isLinkAsset(asset)) {
    const linkType = readUnhandledEnumCase(asset.linkType) || 'WEBSITE';
    return I18n.text(`learningCenter.resources.type.${linkType}`);
  }
  return getHumanizedDuration('duration' in asset ? asset.duration || 0 : 0);
};
const getLessonVideoCount = assets => assets.filter(asset => asset.__typename === VideoAssetTypename).length;
const getLessonChapterCount = assets => assets.filter(asset => asset.__typename === ChapterAssetTypename).length;
const LessonNavigator = ({
  assets,
  active,
  activeAssetId,
  chirpLesson,
  lesson,
  screen,
  track,
  trackId
}) => {
  const {
    isTablet,
    isNotLaptop
  } = useMediaQuery();
  const {
    id,
    name,
    duration,
    useCases
  } = lesson;
  const {
    allVideosHaveCaptions,
    loading
  } = useLessonQuizMeVideoRequestParams({
    contents: assets
  });
  const {
    getLessonPreQuizState
  } = useLessonPreQuizSession();
  const preQuizState = getLessonPreQuizState(id);
  const mappedUseCases = useCases === null || useCases === void 0 ? void 0 : useCases.map(readUnhandledEnumCase);
  const preAssessmentAvailable = isNotLaptop && !loading && allVideosHaveCaptions && chirpLesson && screen;
  return /*#__PURE__*/_jsx(ACLessonNavigator, {
    active: active,
    activeAssetId: activeAssetId,
    assets: assets.map(asset => ({
      id: asset.id,
      title: getAssetTitle(asset),
      helpText: getAssetHelpText(asset),
      complete: !!asset.completed,
      showFocus: showPreAssessmentFocusRibbon({
        contentId: asset.id,
        lessonContent: assets,
        preQuizState
      }),
      lessonId: id
    })),
    courseId: trackId,
    helpText: /*#__PURE__*/_jsxs(List, {
      use: "inline-divided",
      children: [getLessonVideoCount(assets) > 0 && /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.video",
            options: {
              count: getLessonVideoCount(assets)
            }
          })
        })
      }), getLessonChapterCount(assets) > 0 && /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.chapter",
            options: {
              count: getLessonChapterCount(assets)
            }
          })
        })
      }), /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.lessonNavigator.info",
            options: {
              totalDuration: getHumanizedDuration(duration)
            }
          })
        })
      })]
    }),
    lessonId: lesson.id,
    menuIcon: /*#__PURE__*/_jsx(CardMenuIcon, {
      item: lesson,
      use: "transparent"
    }),
    optionalNavStartSteps: preAssessmentAvailable && /*#__PURE__*/_jsx(HasGate, {
      gate: "Academy:LessonPreAssessment",
      children: /*#__PURE__*/_jsx(LessonPreQuizOptionalTab, {
        lesson: chirpLesson,
        screen: screen,
        to: trackId ? `/tracks/${trackId}/${id}/${PRE_ASSESSMENT_URL_FRAGMENT}` : `/lessons/${id}/${PRE_ASSESSMENT_URL_FRAGMENT}`,
        track: track
      })
    }),
    optionalSteps: !isTablet && /*#__PURE__*/_jsx(ToolRecommendationOptionalStep, {
      to: trackId ? `/tracks/${trackId}/${id}/${SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS}` : `/lessons/${id}/${SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS}`,
      useCases: mappedUseCases
    }),
    title: name
  });
};
export default LessonNavigator;